import { type PropsWithChildren } from 'react';
import { ModalProvider } from '@banhala/ui-kit';
import type { ILogging } from '../api/types/ILogging';
import { AnalyticsProvider } from './AnalyticsContext';
import { AndroidBackPressLogger } from './AndroidBackPressLogger';
import { InactiveScreenGate } from './InactiveScreenGate';

type Props = PropsWithChildren<{
  screen: string;
  getDefaults?: () => ILogging | undefined;
  /**
   * 이 화면이 자체 ModalProvider를 호스팅할지 여부.
   * - `true` (기본): 이 화면 범위에서 열리는 Modal이 같은 analytics 컨텍스트 안에 렌더됨
   * - `false`: ModalProvider를 두지 않아 useModal이 상위 Provider까지 bubble up.
   *   BottomTab/TabNavigator 안의 화면처럼, Modal을 탭 UI 위로 띄워야 할 때 사용.
   *   상위(Navigation screenLayout 또는 App Root)의 ModalProvider가 잡게 됨.
   */
  hostsModals?: boolean;
}>;

/**
 * 페이지 레벨 스코프 묶음.
 * - `AnalyticsProvider`: 이 범위 내에서 발화되는 이벤트의 기본 SCREEN / defaults 설정
 * - `ModalProvider` (옵셔널): 이 범위에서 열리는 BottomSheet/Modal이 같은 analytics 컨텍스트 안에 렌더되도록 보장
 *
 * 기존 `AnalyticsProvider` 단독 사용 시 ModalProvider는 앱 상위(혹은 Navigation screenLayout)에
 * 있어서, 거기 등록된 Modal이 AnalyticsProvider 트리 바깥에 렌더됨 → `useAnalyticsContext()`가
 * 기본값(`{}`)을 반환해 로깅 필드 누락이 발생했음. AnalyticsScreen은 내부에 ModalProvider를
 * 한 겹 더 둬서 이 문제를 구조적으로 해결함.
 *
 * BottomTab/TabNavigator 내부 화면처럼 Modal을 탭 UI 위로 띄워야 하는 경우엔 `hostsModals={false}`로
 * ModalProvider를 끄고, 상위 ModalProvider(Stack screenLayout 등)가 잡도록 한다.
 */
export const AnalyticsScreen = ({
  screen,
  getDefaults,
  hostsModals = true,
  children,
}: Props) => {
  // InactiveScreenGate 는 화면 "콘텐츠(children)"만 감싼다. ModalProvider(및 그 modalList)는
  // 이 게이트 밖(위)에 있으므로, 콘텐츠가 비활성 depth 로 unmount 돼도 열려있는 Modal/
  // BottomSheet 는 살아남는다. (게이트를 screenLayout 최상단에 두던 기존 구조에선 ModalProvider
  // 까지 통째로 unmount 되어 모달이 사라졌음)
  const inner = (
    <>
      <AndroidBackPressLogger />
      <InactiveScreenGate>{children}</InactiveScreenGate>
    </>
  );

  return (
    <AnalyticsProvider screen={screen} getDefaults={getDefaults}>
      {hostsModals ? <ModalProvider>{inner}</ModalProvider> : inner}
    </AnalyticsProvider>
  );
};
